import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = user
  const [loading, setLoading] = useState(true);
  const authed = useRef(false);
  const userRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/me")
      .then((res) => mounted && !authed.current && setUser(res.data))
      .catch(() => mounted && !authed.current && setUser(false))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => {
    const onLost = () => {
      if (userRef.current) toast.warning("You were signed out of this device. Sign in again to continue.");
      authed.current = false;
      setUser(false);
    };
    window.addEventListener("kingly:auth-lost", onLost);
    return () => window.removeEventListener("kingly:auth-lost", onLost);
  }, []);

  async function login(email, password) {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.mfa_required) return { ok: false, mfa: data };
      if (data?.approval_required) return { ok: false, approval: data };
      authed.current = true;
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }

  // New device waiting for a trusted device to say "yes, that's me".
  async function pollApproval(approvalId, pollToken, trustDevice) {
    try {
      const { data } = await api.post(`/auth/login/approval/${approvalId}`, { poll_token: pollToken, trust_device: !!trustDevice });
      if (data?.status === "pending") return { ok: false, pending: true };
      authed.current = true;
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, status: e.response?.status, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }

  async function approvalUseCode(approvalId, pollToken) {
    try {
      const { data } = await api.post(`/auth/login/approval/${approvalId}/code`, { poll_token: pollToken });
      return { ok: true, mfa: data };
    } catch (e) {
      return { ok: false, status: e.response?.status, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }

  async function verifyMfa(challengeId, code, trustDevice) {
    try {
      const { data } = await api.post("/auth/login/verify", { challenge_id: challengeId, code, trust_device: !!trustDevice });
      authed.current = true;
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, status: e.response?.status, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }

  async function resendMfa(challengeId, channel) {
    try {
      const { data } = await api.post("/auth/login/resend", { challenge_id: challengeId, ...(channel ? { channel } : {}) });
      return { ok: true, challenge: data };
    } catch (e) {
      return { ok: false, status: e.response?.status, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }

  async function register(email, password, name, barnName, ref = "", phone = "", smsOptIn = true) {
    try {
      const src = localStorage.getItem("kingly_src") || "";
      const refVia = (JSON.parse(localStorage.getItem("kingly_ref") || "null") || {}).via || "";
      const promo = localStorage.getItem("kingly_promo") || "";
      const { data } = await api.post("/auth/register", { email, password, name, barn_name: barnName, ref, ref_via: refVia, src, promo, phone, sms_opt_in: smsOptIn });
      localStorage.removeItem("kingly_ref");
      localStorage.removeItem("kingly_src");
      localStorage.removeItem("kingly_promo");
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }

  async function registerWithInvite(email, password, name, code, phone = "", smsOptIn = true) {
    try {
      const { data } = await api.post("/auth/register-with-invite", { email, password, name, code, phone, sms_opt_in: smsOptIn });
      authed.current = true;
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }

  async function registerTeam(email, password, name, teamName, phone = "") {
    try {
      const { data } = await api.post("/auth/register-team", { email, password, name, team_name: teamName, phone });
      authed.current = true;
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }

  async function resetPassword(email, code, newPassword) {
    const { data } = await api.post("/auth/reset-password", { email, code, new_password: newPassword });
    if (data?.signed_in && data.user) { authed.current = true; setUser(data.user); }
    return data;
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed (clearing session anyway)", err);
    }
    setUser(false);
  }

  const value = useMemo(
    () => ({ user, loading, login, verifyMfa, resendMfa, pollApproval, approvalUseCode, register, registerWithInvite, registerTeam, resetPassword, logout }),
    [user, loading] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
